import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, Trash2, Send, Search, User, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminQuickOrder = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [orderType, setOrderType] = useState(null); // "private" or "business"

  const [orderInfo, setOrderInfo] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_address: "",
    delivery_comune: "",
    delivery_zone: "",
    delivery_date: "",
    delivery_time: "",
    delivery_notes: "",
    admin_note: "",
    payment_method: "Pagamento alla consegna",
    shipping: 5,
  });

  useEffect(() => {
    fetchProducts();
    fetchZones();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?admin_view=true&include_business=true`);
      setProducts(response.data.filter(p => p.active));
    } catch { toast.error("Errore caricamento prodotti"); }
    finally { setLoading(false); }
  };

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API}/delivery-zones`);
      setDeliveryZones(response.data || []);
    } catch { /* ignore */ }
  };

  // Filtra prodotti: privati vedono monoporzione con stock, aziende vedono tutto con stock
  const filteredProducts = products
    .filter(p => {
      if (orderType === "private") return !p.business_only && p.stock > 0;
      if (orderType === "business") return p.stock > 0;
      return true;
    })
    .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const addToCart = (product) => {
    const existing = cart.find(c => c.id === product.id);
    if (existing) {
      setCart(cart.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { id: product.id, name: product.name, price: product.price, quantity: 1, business_only: product.business_only }]);
    }
  };

  const updateQuantity = (id, delta) => {
    setCart(cart.map(c => {
      if (c.id === id) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : c;
      }
      return c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (id) => setCart(cart.filter(c => c.id !== id));

  // Privati: prezzo già IVA inclusa. Aziende: prezzo + IVA 10%
  const subtotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const shipping = parseFloat(orderInfo.shipping) || 0;
  const iva = orderType === "business" ? round2(subtotal * 0.10) : 0;
  const total = round2(subtotal + shipping + iva);

  function round2(n) { return Math.round(n * 100) / 100; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast.error("Aggiungi almeno un prodotto"); return; }
    if (!orderInfo.customer_name) { toast.error("Inserisci il nome del cliente"); return; }
    if (!orderInfo.admin_note) { toast.error("Inserisci una nota per chi è l'ordine"); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/quick-order`, {
        ...orderInfo,
        items: cart,
        shipping: shipping,
        order_type: orderType,
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Ordine #${response.data.order_number} creato! Totale: €${response.data.total.toFixed(2)}`);
      setCart([]);
      setOrderType(null);
      setOrderInfo({
        customer_name: "", customer_phone: "", customer_email: "",
        customer_address: "", delivery_comune: "", delivery_zone: "",
        delivery_date: "", delivery_time: "", delivery_notes: "",
        admin_note: "", payment_method: "Pagamento alla consegna", shipping: 5,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione ordine");
    } finally {
      setSubmitting(false);
    }
  };

  const timeSlots = ["10:00-12:00", "12:00-14:00", "14:00-16:00", "16:00-18:00", "18:00-20:00"];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  // Step 1: Seleziona tipo ordine
  if (!orderType) {
    return (
      <AdminLayout>
        <div data-testid="admin-quick-order">
          <h1 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-green-500" />
            Ordine Rapido
          </h1>
          <p className="text-zinc-400 mb-6">Seleziona il tipo di cliente per questo ordine:</p>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
            <button
              onClick={() => { setOrderType("private"); setCart([]); }}
              className="bg-zinc-900 border-2 border-zinc-700 rounded-xl p-8 hover:border-green-500 transition-colors text-center group"
              data-testid="select-private"
            >
              <User className="w-12 h-12 text-green-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl font-bold text-white mb-2">Privato</h2>
              <p className="text-zinc-400 text-sm">Prezzi IVA inclusa</p>
              <p className="text-zinc-500 text-xs mt-1">Monoporzioni</p>
            </button>
            <button
              onClick={() => { setOrderType("business"); setCart([]); }}
              className="bg-zinc-900 border-2 border-zinc-700 rounded-xl p-8 hover:border-blue-500 transition-colors text-center group"
              data-testid="select-business"
            >
              <Building2 className="w-12 h-12 text-blue-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl font-bold text-white mb-2">Azienda</h2>
              <p className="text-zinc-400 text-sm">Prezzi + IVA 10%</p>
              <p className="text-zinc-500 text-xs mt-1">Gastronomia</p>
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-quick-order">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-green-500" />
            Ordine Rapido
            <span className={`text-sm px-3 py-1 rounded-full ${orderType === "private" ? "bg-green-500/20 text-green-500" : "bg-blue-500/20 text-blue-500"}`}>
              {orderType === "private" ? "Privato" : "Azienda"}
            </span>
          </h1>
          <button
            onClick={() => { setOrderType(null); setCart([]); }}
            className="text-zinc-400 hover:text-white text-sm border border-zinc-700 px-3 py-1 rounded-lg"
          >
            Cambia tipo
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Products */}
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca prodotto..."
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-green-500"
                data-testid="quick-order-search"
              />
            </div>

            <div className="max-h-[500px] overflow-y-auto space-y-1 pr-1">
              {filteredProducts.map(product => (
                <div
                  key={product.id}
                  className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg p-3 hover:border-green-500/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{product.name}</p>
                    <p className="text-green-500 font-semibold text-sm">
                      €{product.price.toFixed(2)}
                      {orderType === "private" && <span className="text-zinc-500 text-xs ml-1">(IVA incl.)</span>}
                      {orderType === "business" && <span className="text-zinc-500 text-xs ml-1">(+IVA: €{(product.price * 1.10).toFixed(2)})</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => addToCart(product)}
                    className="bg-green-500 text-black w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-600 shrink-0 ml-2"
                    data-testid={`add-${product.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Cart + Order Info */}
          <div className="space-y-4">
            {/* Cart */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <h2 className="text-white font-bold mb-3">Carrello ({cart.length})</h2>
              {cart.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-4">Aggiungi prodotti dal catalogo</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center justify-between bg-zinc-800 rounded-lg p-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{item.name}</p>
                        <p className="text-zinc-400 text-xs">€{item.price.toFixed(2)} x {item.quantity}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button onClick={() => updateQuantity(item.id, -1)} className="text-zinc-400 hover:text-white p-1"><Minus className="w-4 h-4" /></button>
                        <span className="text-white font-bold text-sm w-6 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, 1)} className="text-zinc-400 hover:text-white p-1"><Plus className="w-4 h-4" /></button>
                        <button onClick={() => removeFromCart(item.id)} className="text-red-400 hover:text-red-300 p-1 ml-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {cart.length > 0 && (
                <div className="border-t border-zinc-700 mt-3 pt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-zinc-400"><span>Subtotale</span><span>€{subtotal.toFixed(2)}</span></div>
                  {orderType === "business" && <div className="flex justify-between text-zinc-400"><span>IVA (10%)</span><span>€{iva.toFixed(2)}</span></div>}
                  {orderType === "private" && <div className="flex justify-between text-zinc-500 text-xs"><span>IVA inclusa nel prezzo</span><span></span></div>}
                  <div className="flex justify-between text-zinc-400"><span>Spedizione</span><span>€{shipping.toFixed(2)}</span></div>
                  <div className="flex justify-between text-white font-bold text-lg"><span>Totale</span><span className="text-green-500">€{total.toFixed(2)}</span></div>
                </div>
              )}
            </div>

            {/* Order Form */}
            <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
              <h2 className="text-white font-bold mb-2">Dati Ordine</h2>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs">Nome cliente *</label>
                  <input type="text" value={orderInfo.customer_name} onChange={(e) => setOrderInfo({...orderInfo, customer_name: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" required data-testid="qo-name" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs">Telefono</label>
                  <input type="text" value={orderInfo.customer_phone} onChange={(e) => setOrderInfo({...orderInfo, customer_phone: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-phone" />
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs">Indirizzo</label>
                <input type="text" value={orderInfo.customer_address} onChange={(e) => setOrderInfo({...orderInfo, customer_address: e.target.value})}
                  className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-address" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs">Data consegna</label>
                  <input type="date" value={orderInfo.delivery_date} onChange={(e) => setOrderInfo({...orderInfo, delivery_date: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-date" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs">Fascia oraria</label>
                  <select value={orderInfo.delivery_time} onChange={(e) => setOrderInfo({...orderInfo, delivery_time: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-time">
                    <option value="">Seleziona</option>
                    {timeSlots.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs">Comune</label>
                  <input type="text" value={orderInfo.delivery_comune} onChange={(e) => setOrderInfo({...orderInfo, delivery_comune: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-comune" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs">Zona</label>
                  <select value={orderInfo.delivery_zone} onChange={(e) => setOrderInfo({...orderInfo, delivery_zone: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-zone">
                    <option value="">Seleziona zona</option>
                    {deliveryZones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs">Metodo pagamento</label>
                  <select value={orderInfo.payment_method} onChange={(e) => setOrderInfo({...orderInfo, payment_method: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-payment">
                    <option value="Pagamento alla consegna">Contanti alla consegna</option>
                    <option value="sumup">Pagato con carta</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs">Spedizione (€)</label>
                  <input type="number" step="0.5" min="0" value={orderInfo.shipping} onChange={(e) => setOrderInfo({...orderInfo, shipping: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-shipping" />
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs">Nota admin (per chi è l'ordine) *</label>
                <textarea value={orderInfo.admin_note} onChange={(e) => setOrderInfo({...orderInfo, admin_note: e.target.value})}
                  placeholder="Es: Ordine per Mario Rossi - cliente di Spello"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500 h-16 resize-none" required data-testid="qo-admin-note" />
              </div>

              <div>
                <label className="text-zinc-400 text-xs">Note consegna</label>
                <input type="text" value={orderInfo.delivery_notes} onChange={(e) => setOrderInfo({...orderInfo, delivery_notes: e.target.value})}
                  className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="qo-notes" />
              </div>

              <button
                type="submit"
                disabled={submitting || cart.length === 0}
                className="w-full bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="qo-submit"
              >
                <Send className="w-5 h-5" />
                {submitting ? "Invio in corso..." : `Crea Ordine (€${total.toFixed(2)})`}
              </button>
            </form>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminQuickOrder;
